<script setup lang="ts">
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { breakpointsTailwind, useBreakpoints, useEventListener } from '@vueuse/core'
import hovercardDataJson from '~/data/hovercards.generated.json'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  contestant: {
    type: String,
    required: true,
  },
  hover: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: 'bottom',
  },
})

interface HovercardScore {
  TimePeriod: string
  Wins: number
  Losses: number
}

interface HovercardEntry {
  contestantDetails: {
    name: string
    twitter: string
  }
  lastWinningShow: {
    yt_link: string
    yt_title: string
  }
  scoresByYearWon: HovercardScore[]
  showDate: string
  ytThumbnailUrl: string
}

const attrs = useAttrs()
const breakpoints = useBreakpoints(breakpointsTailwind)
const largerThanMd = breakpoints.greater('md')
const triggerEl = ref<HTMLElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const isPositioned = ref(false)
const hovercardData = hovercardDataJson as Record<string, HovercardEntry>
const hovercard = computed<HovercardEntry | null>(() => hovercardData[props.contestant.toLowerCase()] ?? null)
const panelStyle = ref<Record<string, string>>({
  left: '0px',
  pointerEvents: 'none',
  position: 'fixed',
  top: '0px',
  visibility: 'hidden',
  zIndex: '9999',
})

let openTimer: ReturnType<typeof setTimeout> | null = null
let closeTimer: ReturnType<typeof setTimeout> | null = null

function clearTimer(timer: 'open' | 'close') {
  const timerRef = timer === 'open' ? openTimer : closeTimer

  if (timerRef) {
    clearTimeout(timerRef)
  }

  if (timer === 'open') {
    openTimer = null
  }
  else {
    closeTimer = null
  }
}

function hidePanel() {
  panelStyle.value = {
    left: '0px',
    pointerEvents: 'none',
    position: 'fixed',
    top: '0px',
    visibility: 'hidden',
    zIndex: '9999',
  }
}

function nextFrame() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })
}

function updatePosition() {
  if (!triggerEl.value || !panelEl.value || !isOpen.value || !hovercard.value) {
    return
  }

  const gap = 12
  const viewportPadding = 12
  const triggerRect = triggerEl.value.getBoundingClientRect()
  const panelRect = panelEl.value.getBoundingClientRect()

  let top = triggerRect.bottom + gap
  let left = triggerRect.left + (triggerRect.width - panelRect.width) / 2

  switch (props.placement) {
    case 'right':
      top = triggerRect.top + (triggerRect.height - panelRect.height) / 2
      left = triggerRect.right + gap
      break
    case 'left':
      top = triggerRect.top + (triggerRect.height - panelRect.height) / 2
      left = triggerRect.left - panelRect.width - gap
      break
    case 'top':
      top = triggerRect.top - panelRect.height - gap
      left = triggerRect.left + (triggerRect.width - panelRect.width) / 2
      break
    case 'bottom':
    default:
      break
  }

  const maxLeft = Math.max(viewportPadding, window.innerWidth - panelRect.width - viewportPadding)
  const maxTop = Math.max(viewportPadding, window.innerHeight - panelRect.height - viewportPadding)

  panelStyle.value = {
    left: `${Math.max(viewportPadding, Math.min(left, maxLeft))}px`,
    pointerEvents: 'auto',
    position: 'fixed',
    top: `${Math.max(viewportPadding, Math.min(top, maxTop))}px`,
    visibility: 'visible',
    zIndex: '9999',
  }

  isPositioned.value = true
}

async function openCard() {
  if (!props.hover || !largerThanMd.value || !hovercard.value) {
    return
  }

  clearTimer('close')
  clearTimer('open')

  openTimer = setTimeout(async () => {
    isPositioned.value = false
    hidePanel()
    isOpen.value = true

    await nextTick()
    await nextFrame()
    updatePosition()
  }, 100)
}

function closeCard() {
  clearTimer('open')
  clearTimer('close')

  closeTimer = setTimeout(() => {
    isOpen.value = false
    isPositioned.value = false
    hidePanel()
  }, 200)
}

function keepOpen() {
  clearTimer('close')
}

watch(largerThanMd, (matches) => {
  if (!matches) {
    isOpen.value = false
    isPositioned.value = false
    hidePanel()
  }
})

if (import.meta.client) {
  useEventListener(window, 'resize', updatePosition)
  useEventListener(window, 'scroll', updatePosition, { capture: true, passive: true })
}

onBeforeUnmount(() => {
  clearTimer('open')
  clearTimer('close')
})
</script>

<template>
  <div class="inline-block" v-bind="attrs">
    <div
      ref="triggerEl"
      @focusin="openCard"
      @focusout="closeCard"
      @mouseenter="openCard"
      @mouseleave="closeCard"
    >
      <slot />
    </div>

    <Teleport v-if="hovercard" to="body">
      <div
        v-show="isOpen && largerThanMd"
        ref="panelEl"
        :aria-hidden="!isPositioned"
        :style="panelStyle"
        @mouseenter="keepOpen"
        @mouseleave="closeCard"
      >
        <div class="w-65 p-3 bg-white rounded-md shadow-green-400 border-4 border-[#ec0007] dark:bg-gray-700">
          <div class="mb-2 flex justify-between items-center pb-2 border-b-1.5 border-green-300">
            <div class="font-bold sg-bold text-xl">
              {{ hovercard.contestantDetails.name }}
            </div>
            <div v-if="hovercard.contestantDetails.twitter">
              <NuxtImg
                loading="lazy"
                :src="`/images/twitter-avatars/${hovercard.contestantDetails.twitter}.jpg`"
                class="rounded-full object-contain h-10 w-10 hover:blur-[2px] bg-white"
              />
            </div>
          </div>

          <TabGroup>
            <TabList
              class="text-xs uppercase sg-medium space-x-1 h-7 overflow-x-scroll flex scrollbar scrollbar-h-4px scrollbar-rounded scrollbar-thumb-color-gray-200 scrollbar-radius-color-green-300 focus-visible:outline-none focus:outline-none dark:text-white"
            >
              <Tab
                v-for="score in hovercard.scoresByYearWon"
                :key="score.TimePeriod"
                v-slot="{ selected }"
              >
                <div
                  class="rounded-lg px-1.5 hover:bg-green-100 flex ml-3px focus-visible:outline-none focus:outline-none dark:hover:bg-green-400 dark:hover:text-gray-800"
                  :class="selected ? 'bg-green-100 text-green-900 ring-green-400 ring-2 dark:bg-green-300 dark:ring-green-600' : ''"
                >
                  <div v-if="score.TimePeriod === 'All Time'" class="flex-shrink-0 w-12">
                    {{ score.TimePeriod }}
                  </div>
                  <div v-else>
                    {{ score.TimePeriod }}
                  </div>
                </div>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel
                v-for="score in hovercard.scoresByYearWon"
                :key="score.TimePeriod"
              >
                <div class="sg-medium tracking-wide flex justify-center py-2 pb-2 border-b-1.5 border-green-300">
                  <div class="flex flex-col dark:text-white">
                    <span class="inline-block w-30 text-center">WINS - {{ score.Wins }}</span>
                    <span class="inline-block w-30 text-center">LOSSES - {{ score.Losses }}</span>
                  </div>
                </div>
              </TabPanel>
            </TabPanels>
          </TabGroup>

          <div class="sg-regular text-sm pb-2 pt-2 select-none dark:text-white flex items-center">
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" class="fill-green-500 h-8 w-8" viewBox="0 0 512 512"><path
                d="M256 25c-11.594 0-23 12.8-23 31s11.406 31 23 31s23-12.8 23-31s-11.406-31-23-31zm-103.951 2.975l-16.098 8.05c15.092 30.185 51.37 56.81 82.188 74.442L232.334 295H247V192h18v103h14.666l14.195-184.533c30.818-17.632 67.096-44.257 82.188-74.442l-16.098-8.05c-19.91 29.9-44.891 49.148-71.334 57.77C281.311 97.28 269.75 105 256 105c-13.75 0-25.31-7.72-32.617-19.256c-26.443-8.62-51.424-27.87-71.334-57.77zM169 313v96H25v78h462v-30H343V313H169z"
              /></svg>
            </span>
            Last win on {{ hovercard.showDate }}
          </div>

          <NuxtLink
            target="_blank"
            :to="hovercard.lastWinningShow.yt_link"
            class="flex group no-select cursor-pointer"
          >
            <div class="relative">
              <div
                class="absolute z-100 right-0 h-6 w-6 m-2 transition duration-500 transform group-hover:-translate-y-1 group-hover:-translate-x-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    class="fill-none stroke-white group-hover:block hidden -z-10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m4-3h6v6m-11 5L21 3"
                  />
                </svg>
              </div>
              <NuxtImg
                :alt="hovercard.lastWinningShow.yt_title"
                :src="hovercard.ytThumbnailUrl"
                loading="lazy"
                format="webp"
                class="crapShadow aspect-video rounded-lg object-cover object-center w-60 transition duration-500 transform group-hover:-translate-y-1 group-hover:-translate-x-2"
              />
            </div>
          </NuxtLink>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style>
.crapShadow:hover {
  box-shadow: 4px 4px #00dc82;
}
</style>
